import { Link } from 'react-router-dom';
import type { ParticipantProgressDto } from '@genoma/shared';

import { useI18n } from '../../i18n/index.tsx';

/**
 * The most recently active people, as a table rather than a chart.
 *
 * Past about seven classes that all carry meaning, more colour stops helping —
 * the reader wants to find one person and read their row. The only graphic
 * element is a thin participation track, which is what the eye scans for.
 *
 * The card is its own height, not the column's. Stretching it to match the
 * panel beside it left a few hundred pixels of empty card under the last row
 * whenever that panel ran long — which reads as a list that lost its rows,
 * not as harmony.
 */
export const ParticipantTable = ({
  participants,
}: {
  participants: ParticipantProgressDto[];
}) => {
  const { t } = useI18n();

  return (
    <div className="overflow-x-auto rounded-2xl border border-line bg-paper shadow-card">
      <table className="w-full min-w-[720px] border-collapse text-left">
        <thead>
          <tr className="border-b border-line">
            {[
              'dashboard.colName',
              'dashboard.colParticipation',
              'dashboard.colSelf',
              'dashboard.colOutlook',
              'dashboard.colReport',
            ].map((key) => (
              <th
                key={key}
                className="px-4 py-2.5 font-mono text-[9.5px] font-bold tracking-[0.12em] whitespace-nowrap text-faint uppercase"
              >
                {t(key)}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {participants.map((participant) => (
            <tr
              key={participant.participantId}
              className="relative border-t border-line-soft transition hover:bg-tint-soft"
            >
              <td className="px-4 py-2.5">
                <span className="flex items-center gap-2">
                  {/* One real link, stretched over the row: the whole row is a
                      hit target without inventing a clickable <tr>, and the
                      accessible name stays the person's name. */}
                  <Link
                    to={`/participantes/${participant.participantId}`}
                    className="text-[13.5px] font-bold after:absolute after:inset-0 after:content-['']"
                  >
                    {participant.fullName}
                  </Link>
                  {participant.keyLeader && (
                    <span className="rounded-full border border-tint-line bg-tint px-1.5 py-px font-mono text-[8.5px] tracking-[0.1em] text-teal-deep uppercase">
                      {t('dashboard.keyLeader')}
                    </span>
                  )}
                </span>
                <span className="block text-[12px] text-soft">
                  {participant.jobTitle ?? t(`roleLevels.${participant.roleLevel}`)}
                </span>
              </td>

              <td className="px-4 py-2.5 align-middle">
                <span className="flex items-center gap-2.5">
                  <span className="h-1.5 w-24 shrink-0 rounded-full bg-line-soft">
                    <span
                      className="block h-full rounded-full bg-teal"
                      style={{ width: `${participant.participationRate}%` }}
                    />
                  </span>
                  <span className="font-mono text-[12px] tabular-nums text-soft">
                    {participant.respondedRaters}/{participant.invitedRaters}
                  </span>
                </span>
              </td>

              <td className="px-4 py-2.5 text-[13px]">
                {participant.selfAssessmentDone ? (
                  <span className="text-teal-deep">{t('dashboard.done')}</span>
                ) : (
                  <span className="text-faint">{t('dashboard.pending')}</span>
                )}
              </td>

              {/* What the report would look like if the cycle closed now — the
                  column P&O can still act on. */}
              <td className="px-4 py-2.5 text-[13px]">
                {participant.reportBlocked ? (
                  <span className="flex items-center gap-1.5">
                    <span aria-hidden className="text-[10px] text-[var(--color-critical)]">
                      ●
                    </span>
                    <span className="text-ink">{t('dashboard.outlookBlocked')}</span>
                  </span>
                ) : participant.thinSegments.length > 0 ? (
                  <span className="flex items-center gap-1.5">
                    <span aria-hidden className="text-[10px] text-[var(--color-warning)]">
                      ▲
                    </span>
                    <span className="text-ink">
                      {participant.thinSegments
                        .map((segment) => t(`relationships.${segment}`))
                        .join(', ')}
                    </span>
                  </span>
                ) : participant.globalOnly ? (
                  <span className="text-soft">{t('dashboard.outlookGlobalOnly')}</span>
                ) : (
                  <span className="text-soft">{t('dashboard.outlookSegmented')}</span>
                )}
              </td>

              <td className="px-4 py-2.5 text-[13px] whitespace-nowrap text-soft">
                {participant.reportStatus
                  ? t(`reportStatus.${participant.reportStatus}`)
                  : t('dashboard.notGenerated')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
